import { SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Col, Input, message, Pagination, Popconfirm, Row, Space, Table, Tooltip } from 'antd';
import { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from "../../Images/ArchiveRestore.svg";
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { fetchArchivedCustomerList, unArchiveCustomer } from '../../slices/customer';

function ArchiveCustomerList() {
    const [search, setSearch] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]); // Track selected rows
    const [page, setPage] = useState(1);
    const [pagesize, setPageSize] = useState(10);
    const [orderBy, setOrderBy] = useState('');

    const dispatch = useDispatch();
    const { data = [], totalRecord = 0, status } = useSelector(state => state.customer || {});
    useEffect(() => {
        dispatch(fetchArchivedCustomerList({ page, pagesize, ...(search && { search: search }), ...(orderBy && { ordering: orderBy }) }));
    }, [dispatch, page, pagesize, search, orderBy]);

    const handlePageSizeChange = (current, size) => {
        setPage(1); // Reset to first page if needed
        setPageSize(size); // You must have a setter or state update
    };

    const handleSearch = e => {
        setSearch(e.target.value);
        setPage(1); // Reset to the first page when searching
    };

    const resetFilter = () => {
        setSearch('');
        setPage(1);
        setOrderBy('');
    };

    const handlePageChange = newPage => {
        setPage(newPage);
    };
    const GetSortingEnumValue = (sortField, sortOrder) => {
        let sortingEnumKey = "id";

        if (sortField === "customer_name" && sortOrder === "ascend") {
            sortingEnumKey = "customer_name";
        }
        if (sortField === "customer_name" && sortOrder === "descend") {
            sortingEnumKey = "-customer_name";
        }
        if (sortField === "phone_number" && sortOrder === "ascend") {
            sortingEnumKey = "phone_number";
        }
        if (sortField === "phone_number" && sortOrder === "descend") {
            sortingEnumKey = "-phone_number";
        }


        return sortingEnumKey;
    };
    const handleTableChange = async (pagination, filters, sorter, ordering) => {
        if (sorter && sorter.columnKey && sorter.order) {
            const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
            setOrderBy(orderBy);
        }
        setPageSize(pagination.pageSize);
    };
    const dataSource = data.map((item, index) => ({
        ...item,
        key: item.id || index,
        SrNo: (page - 1) * pagesize + index + 1, // Calculate Sr. No. based on page and limit
    }));


    const handleRestore = (id) => {
        if (!id) {
            message.error('Missing ID for deletion!');
            return;
        }

        dispatch(unArchiveCustomer(id))
            .unwrap()
            .then(() => {
                message.success('Customer Unarchived successfully!');
                dispatch(fetchArchivedCustomerList({ page, pagesize, ...(search && { search: search }), ...(orderBy && { ordering: orderBy }) }));
            })
            .catch((error) => {
                if (typeof error === 'object' && error !== null) {
                    const messages = [];
                    for (const key in error) {
                        if (Array.isArray(error[key])) {
                            messages.push(`${key}: ${error[key].join(', ')}`);
                        }
                    }
                    if (messages.length > 0) {
                        message.error(messages.join('; '));
                    } else {
                        message.error('An unknown error occurred.');
                    }
                } else if (typeof error === 'string') {
                    message.error(error); // Handle string error
                } else if (error instanceof Error) {
                    message.error(error.message); // Handle Error object
                } else {
                    message.error('An unknown error occurred.');
                }
            });
    };

    const columns = [
        {
            title: 'Sr. No.',
            dataIndex: 'SrNo', // Use a unique key for the serial number
            key: 'SrNo',
            sorter: true,
            width: '8%',
        },
        {
            title: 'Name',
            dataIndex: 'customer_name',
            key: 'customer_name',
            sorter: true,
        },
        {
            title: 'Factory Address',
            dataIndex: 'factoryAddress',
            key: 'factoryAddress',
            width: 200,
            sorter: false,
            render: (text, record) => {
                return (
                    <span>
                        {record?.factory_address_shop ||
                            record?.factory_address_area ||
                            record?.factory_address_landmark ||
                            record?.factory_address_city ||
                            record?.factory_address_state ||
                            record?.factory_address_pin_code
                            ? `${record?.factory_address_shop || ''} ${record?.factory_address_area || ''} ${record?.factory_address_landmark || ''} ${record?.factory_address_city || ''} ${record?.factory_address_state || ''}-${record?.factory_address_pin_code || ''}`.trim()
                            : 'N/A'}
                    </span>
                )
            }
        },
        {
            title: 'Office Address',
            dataIndex: 'officeAddress',
            key: 'officeAddress',
            width: 200,
            sorter: false,
            render: (text, record) => {
                return (
                    <span>
                        {record?.office_address_shop ||
                            record?.office_address_area ||
                            record?.office_address_landmark ||
                            record?.office_address_city ||
                            record?.office_address_state ||
                            record?.office_address_pin_code
                            ? `${record?.office_address_shop || ''} ${record?.office_address_area || ''} ${record?.office_address_landmark || ''} ${record?.office_address_city || ''} ${record?.office_address_state || ''}-${record?.office_address_pin_code || ''}`.trim()
                            : 'N/A'}
                    </span>
                )
            }
        },
        {
            title: 'Contact Number',
            dataIndex: 'phone_number',
            key: 'phone_number',
            sorter: true,
            render: (record) => {
                return (
                    <span>+{record} </span>
                )
            }
        },
        {
            title: 'Archive At',
            dataIndex: 'archive_at',
            key: 'archive_at',
            sorter: true,
            render: (record) => {
                return (
                    <span>{dayjs(record).format('DD-MM-YYYY HH:mm')} </span>
                )
            }
        },
        {
            title: 'Archive By',
            dataIndex: 'archive_by',
            key: 'archive_by',
            sorter: true,
            render: (record) => {
                return (
                    <span>{record ? `${record.first_name}${record.last_name}` : 'N/A'} </span>
                )
            }

        },
        {
            title: 'Action',
            dataIndex: 'action',
            fixed: 'right',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Tooltip placement="top" title="Restore">
                        <Popconfirm
                            title="Are you sure you want to Unarchive this record?"
                            onConfirm={() => handleRestore(record.id)} // Trigger delete on confirmation
                            onCancel={() => message.info('Action cancelled')} // Optional cancel action
                            okText="Yes"
                            cancelText="No"
                        >
                            <ArchiveRestore className="ArchiverestorImg" />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];
    const rowSelection = {
        selectedRowKeys,
        onChange: selectedRowKeys => setSelectedRowKeys(selectedRowKeys),
    };

    return (
        <>
            <div className='sticyaction'>
                <Row className="pagenamerow" justify="space-between" align="middle">
                    <Col>

                        <h2>Archived Customers</h2>
                        <div className="bredcrumbwrp">
                            <Link to="/customer-list" className="back">
                                <Back /> BACK
                            </Link>
                            <Breadcrumb
                                items={[
                                    { title: <Link to="/customer-list">Customers</Link> },
                                    { title: 'Archived Customers' }
                                ]}
                            />
                        </div>
                    </Col>
                    <Col>
                        <Space size="small">
                            <Input
                                placeholder="Search"
                                className='searchbox'
                                prefix={<SearchOutlined />}
                                value={search}
                                onChange={handleSearch}
                                style={{ width: '100%' }}
                            />
                            <Tooltip placement="top" title={'Reset Filter'}>
                                <Button type="primary" disabled={!search && !orderBy} className='iconlink' onClick={resetFilter}>
                                    <FilterIcon />
                                </Button>
                            </Tooltip>
                        </Space>
                    </Col>
                </Row>
            </div>
            <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ flex: 1, overflow: 'auto' }}>
                    <Table
                        //rowSelection={rowSelection}
                        dataSource={dataSource}
                        columns={columns}
                        scroll={{
                            x: 280, // Horizontal Scroll
                            y: 500,  // Vertical Scroll with Fixed Header
                        }}
                        loading={status === 'loading'}
                        // pagination={{
                        //     defaultCurrent: page,
                        //     defaultPageSize: pagesize,
                        //     total: totalRecord,
                        //     showTotal: (total) => `Total ${total} items`,
                        //     showSizeChanger: true,
                        //     onChange: handlePageChange,
                        //     locale: {
                        //         items_per_page: 'Items / Page', // Text for the size changer
                        //     },
                        // }}
                        pagination={false}
                        onChange={handleTableChange}
                        style={{ marginTop: '20px' }}
                    />
                </div>
                <div
                    style={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 1000,
                        background: '#fff',
                        padding: 10,
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Pagination
                        current={page}
                        pageSize={pagesize}
                        total={totalRecord}
                        showSizeChanger
                        onShowSizeChange={handlePageSizeChange}
                        showTotal={total => `Total ${total} items`}
                        onChange={handlePageChange}
                        locale={{ items_per_page: 'Items / Page' }}
                    />
                </div>
            </div>
        </>
    );
}

export default ArchiveCustomerList;
